import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import ReCAPTCHA from "react-google-recaptcha";
import { useNavigate } from "react-router-dom";

// ==========================================
// CONFIGURATION & ENDPOINTS
// ==========================================
const API_BASE_URL = process.env.REACT_APP_API_URL || "";
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || "";

const ENDPOINTS = {
  SETTINGS: `${API_BASE_URL}/settings`,
  ADMIN_LOGIN: `${API_BASE_URL}/api/admin/auth/login`,
};

const FALLBACK_SETTINGS = {
  siteTitle: "EarnMedia",
  siteLogo: "https://earnmedia.in/logo.png",
  siteDescription: "Admin access only. Please enter your credentials.",
  favicon: null,
};

export default function AdminLogin() {
  // Brand & Settings State
  const [settings, setSettings] = useState(FALLBACK_SETTINGS);

  // Login State
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({ email: "", password: "", captcha: "" });
  const navigate = useNavigate();

  const recaptchaRef = useRef(null);

  // Helper to store session tokens, set admin cookie, & redirect
  const handleAuthSuccess = (data) => {
    const token = data.token || data.accessToken || data.jwt || data.data?.token;
    if (token) {
      document.cookie = `adminToken=${encodeURIComponent(token)}; path=/; max-age=${365 * 24 * 60 * 60}; SameSite=Lax`;
      // Also store as authToken so existing guards still work if needed
       }

    navigate('/emAdmin7')
  };

  // Fetch Branding Settings
  useEffect(() => {
    let isMounted = true;
    const fetchSettings = async () => {
      try {
        const response = await axios.get(ENDPOINTS.SETTINGS, { timeout: 5000 });
        if (isMounted && response.data) {
          const resData = response.data.data || response.data;
          const data = {
            siteTitle: resData.siteTitle || FALLBACK_SETTINGS.siteTitle,
            siteLogo: resData.siteLogo || FALLBACK_SETTINGS.siteLogo,
            siteDescription: resData.siteDescription || FALLBACK_SETTINGS.siteDescription,
            favicon: resData.favicon || FALLBACK_SETTINGS.favicon,
          };
          setSettings(data);
          updateBranding(data);
        }
      } catch (err) {
        if (isMounted) updateBranding(FALLBACK_SETTINGS);
      }
    };

    fetchSettings();
    return () => { isMounted = false; };
  }, []);

  const updateBranding = (data) => {
    if (data.siteTitle) document.title = `${data.siteTitle} - Admin Login`;
    if (data.favicon) {
      let link = document.querySelector("link[rel*='icon']") || document.createElement("link");
      link.rel = "shortcut icon";
      link.href = data.favicon;
      document.getElementsByTagName("head")[0].appendChild(link);
    }
  };

  // Client Validation
  const validate = () => {
    const errors = { email: "", password: "", captcha: "" };
    let isValid = true;

    if (!email.trim()) {
      errors.email = "Email is required.";
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      errors.email = "Please enter a valid email address.";
      isValid = false;
    }

    if (!password) {
      errors.password = "Password is required.";
      isValid = false;
    }

    if (!captchaToken && RECAPTCHA_SITE_KEY) {
      errors.captcha = "Please complete the CAPTCHA verification.";
      isValid = false;
    }

    setFieldErrors(errors);
    return isValid;
  };

  // Handle Admin Login Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!validate()) return;
    setLoading(true);

    try {
      const payload = {
        email: email.trim(),
        password,
        ...(captchaToken && { cfToken: captchaToken, captchaToken }),
      };

      const response = await axios.post(
        ENDPOINTS.ADMIN_LOGIN,
        payload,
        { headers: { "Content-Type": "application/json" } }
      );

      const data = response.data;

      // Expected: { status: true, msg: "...", token: "..." }
      if (data && (data.status === true || data.success || data.token)) {
        handleAuthSuccess(data);
      } else {
        throw new Error(data.msg || data.message || "Invalid admin credentials.");
      }
    } catch (err) {
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
        setCaptchaToken(null);
      }
      handleAxiosError(err, setError);
    } finally {
      setLoading(false);
    }
  };

  // Generic Error Handler
  const handleAxiosError = (err, setErrorState) => {
    if (err.response) {
      const status = err.response.status;
      const msg = err.response.data?.msg || err.response.data?.message || err.response.data?.error;
      switch (status) {
        case 400: setErrorState(msg || "Invalid request parameters."); break;
        case 401:
        case 404: setErrorState(msg || "Invalid admin email or password."); break;
        case 403: setErrorState(msg || "Your admin account has been disabled."); break;
        case 422: setErrorState(msg || "Validation error. Please verify input."); break;
        case 429: setErrorState("Too many attempts. Please try again later."); break;
        default: setErrorState(msg || "Server error occurred. Please try again later.");
      }
    } else if (err.request) {
      setErrorState("Unable to connect to server. Check network connection.");
    } else {
      setErrorState(err.message || "An unexpected error occurred.");
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#0f0f0f] flex items-center justify-center p-4 sm:p-6 lg:p-8 relative overflow-hidden font-sans antialiased selection:bg-[#059669] selection:text-white transition-colors duration-500">

      {/* ================= BACKGROUND GRADIENT ACCENTS ================= */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
        <div className="absolute inset-0 opacity-[0.04] bg-[radial-gradient(#ffffff_1px,transparent_1px)] [background-size:24px_24px] transition-opacity duration-500" />
        <div className="absolute -top-32 -left-32 w-[34rem] h-[34rem] bg-gradient-to-tr from-[#000000] via-[#059669]/20 to-[#10b981]/15 rounded-full blur-[100px] animate-[pulse_9s_infinite_ease-in-out] transition-all duration-700" />
        <div className="absolute -bottom-40 -right-40 w-[38rem] h-[38rem] bg-gradient-to-br from-[#059669]/15 via-[#000000] to-[#047857]/20 rounded-full blur-[120px] animate-[pulse_12s_infinite_ease-in-out] [animation-delay:3s] transition-all duration-700" />
      </div>

      {/* ================= MAIN CONTAINER CARD ================= */}
      <main className="w-full max-w-[420px] relative z-10 transition-all duration-500 ease-out transform translate-y-0 opacity-100">
        <div className="bg-[#141414]/90 backdrop-blur-xl rounded-xl border border-white/10 shadow-[0_20px_50px_rgba(0,0,0,0.6)] p-7 sm:p-9 transition-all duration-300 ease-out hover:border-[#059669]/40 hover:shadow-[0_25px_60px_rgba(5,150,105,0.15)]">

          {/* ADMIN BADGE */}
          <div className="flex justify-center mb-4">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#059669]/15 border border-[#059669]/40 text-[#10b981] text-[10px] font-black uppercase tracking-widest">
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              Admin Access
            </span>
          </div>

          {/* BRANDING HEADER */}
          <div className="text-center mb-8 transition-all duration-300">
            <div className="inline-block group mb-3">
              {settings.siteLogo ? (
                <img
                  src={settings.siteLogo}
                  alt={settings.siteTitle}
                  className="h-10 w-auto mx-auto object-contain transition-all duration-300 ease-out group-hover:scale-105"
                />
              ) : (
                <div className="w-11 h-11 mx-auto rounded-lg bg-[#059669] text-white flex items-center justify-center font-extrabold text-xl shadow-[0_4px_14px_rgba(5,150,105,0.4)] transition-all duration-300 ease-out group-hover:scale-105 group-hover:bg-[#047857]">
                  {settings.siteTitle ? settings.siteTitle.charAt(0).toUpperCase() : "A"}
                </div>
              )}
            </div>

            <h1 className="text-2xl font-bold text-white tracking-tight mb-1.5 transition-colors duration-300">
              Admin Login
            </h1>
            <p className="text-xs text-gray-400 leading-relaxed max-w-xs mx-auto font-normal transition-colors duration-300">
              {settings.siteDescription}
            </p>
          </div>

          {/* ERROR ALERT */}
          {error && (
            <div
              role="alert"
              className="mb-5 p-3 rounded-lg bg-rose-950/40 border border-rose-800/50 text-rose-300 text-xs flex items-start gap-2.5 transition-all duration-300 ease-out animate-[fadeIn_0.2s_ease-out]"
            >
              <svg className="w-4 h-4 text-rose-400 shrink-0 mt-0.5 transition-transform duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="transition-opacity duration-300">{error}</span>
            </div>
          )}

          {/* LOGIN FORM */}
          <form onSubmit={handleSubmit} noValidate className="space-y-4">

            {/* EMAIL FIELD */}
            <div>
              <div className="relative">
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (fieldErrors.email) setFieldErrors(prev => ({ ...prev, email: "" }));
                  }}
                  placeholder=" "
                  aria-invalid={!!fieldErrors.email}
                  aria-describedby={fieldErrors.email ? "email-error" : undefined}
                  className={`peer w-full px-3.5 pt-5 pb-1.5 text-sm text-white bg-black/40 border ${fieldErrors.email ? "border-rose-500/80 bg-rose-950/10" : "border-white/10"
                    } rounded-lg outline-none transition-all duration-300 ease-out focus:bg-black/60 focus:border-[#059669] focus:ring-2 focus:ring-[#059669]/20`}
                />
                <label
                  htmlFor="email"
                  className="absolute text-xs text-gray-400 duration-300 ease-out transform -translate-y-2 scale-90 top-3.5 z-10 origin-[0] left-3.5 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:text-xs peer-placeholder-shown:text-gray-400 peer-focus:scale-90 peer-focus:-translate-y-2 peer-focus:text-[#059669] pointer-events-none transition-all"
                >
                  Admin Email
                </label>
              </div>
              {fieldErrors.email && (
                <p id="email-error" className="mt-1 ml-1 text-xs text-rose-400 transition-all duration-200 ease-out">
                  {fieldErrors.email}
                </p>
              )}
            </div>

            {/* PASSWORD FIELD */}
            <div>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (fieldErrors.password) setFieldErrors(prev => ({ ...prev, password: "" }));
                  }}
                  placeholder=" "
                  aria-invalid={!!fieldErrors.password}
                  aria-describedby={fieldErrors.password ? "password-error" : undefined}
                  className={`peer w-full pl-3.5 pr-11 pt-5 pb-1.5 text-sm text-white bg-black/40 border ${fieldErrors.password ? "border-rose-500/80 bg-rose-950/10" : "border-white/10"
                    } rounded-lg outline-none transition-all duration-300 ease-out focus:bg-black/60 focus:border-[#059669] focus:ring-2 focus:ring-[#059669]/20`}
                />
                <label
                  htmlFor="password"
                  className="absolute text-xs text-gray-400 duration-300 ease-out transform -translate-y-2 scale-90 top-3.5 z-10 origin-[0] left-3.5 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:text-xs peer-placeholder-shown:text-gray-400 peer-focus:scale-90 peer-focus:-translate-y-2 peer-focus:text-[#059669] pointer-events-none transition-all"
                >
                  Password
                </label>

                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-white focus:outline-none transition-colors duration-200"
                >
                  {showPassword ? (
                    <svg className="w-4 h-4 transition-transform duration-200 active:scale-90" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M13.875 18.825A10.05 10.05 0 0112 19c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M3 3l18 18" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4 transition-transform duration-200 active:scale-90" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
              {fieldErrors.password && (
                <p id="password-error" className="mt-1 ml-1 text-xs text-rose-400 transition-all duration-200 ease-out">
                  {fieldErrors.password}
                </p>
              )}
            </div>

            {/* RECAPTCHA CONTAINER */}
            {RECAPTCHA_SITE_KEY ? (
              <div className="pt-1 transition-all duration-300">
                <div className="flex justify-center overflow-hidden rounded-lg border border-white/10 p-1 bg-black/30 transition-all duration-300 hover:border-white/20">
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={RECAPTCHA_SITE_KEY}
                    onChange={(token) => {
                      setCaptchaToken(token);
                      if (fieldErrors.captcha) setFieldErrors(prev => ({ ...prev, captcha: "" }));
                    }}
                    onExpired={() => setCaptchaToken(null)}
                  />
                </div>
                {fieldErrors.captcha && (
                  <p className="mt-1 text-center text-xs text-rose-400 transition-all duration-200 ease-out">
                    {fieldErrors.captcha}
                  </p>
                )}
              </div>
            ) : null}

            {/* SIGN IN BUTTON */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#059669] hover:bg-[#047857] text-white font-medium py-3 px-4 rounded-lg shadow-[0_4px_16px_rgba(5,150,105,0.3)] hover:shadow-[0_6px_20px_rgba(5,150,105,0.4)] active:scale-[0.99] transition-all duration-300 ease-out disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <span className="flex items-center justify-center gap-2 text-sm tracking-wide transition-all duration-300">
                {loading ? (
                  <>
                    <svg className="animate-spin h-4 w-4 text-white transition-opacity duration-300" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    <span>Signing in...</span>
                  </>
                ) : (
                  <span>Sign In</span>
                )}
              </span>
            </button>

          </form>

          {/* DONT HAVE AN ACCOUNT REDIRECT */}
          <div className="mt-6 text-center text-xs text-gray-400">
            Not an admin?{" "}
            <button
              onClick={() => navigate("/login")}
              className="font-medium text-[#059669] hover:text-[#047857] hover:underline transition-colors duration-200 ease-out"
            >
              Go to User Login
            </button>
          </div>

        </div>
      </main>

    </div>
  );
}